import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useUser } from '../../shared/context/UserContext';
import { useProject } from '../../shared/context/ProjectContext';
import { getRegistrySites, getRegistryTools, searchRegistryTools } from '../../api/assetRegistryApi';
import { MIN_QUERY_LENGTH } from '../../shared/searchFields';
import AssetToolList from './AssetToolList';
import AssetToolDetail from './AssetToolDetail';
import GridView from '../../components/GridView/GridView';
import GridModeToggle from '../../components/GridView/GridModeToggle';
import { useGridState, sortRecords } from '../../components/GridView/useGridState';
import { ASSET_REGISTRY_COLUMNS } from '../../components/GridView/assetRegistryColumnManifest';
import { useLeftPanelCollapse } from '../../shared/hooks/useLeftPanelCollapse';
import LeftPanelCollapseToggle from '../../shared/components/LeftPanelCollapseToggle';
import { useScrollToTopOnMount } from '../../shared/hooks/useScrollToTopOnMount';
import { apiFetch } from '../../auth/apiFetch';
import './assetRegistry.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/* ── Splitter persistence ─────────────────────────────────────────────── */
const SPLIT_MIN = 0.25;
const SPLIT_MAX = 0.65;
const SPLIT_DEFAULT = 0.40;

function getSplitKey(email) {
  return `tmg_asset_registry_split_ratio_${email || 'default'}`;
}
function loadSplitRatio(email) {
  try {
    const v = parseFloat(localStorage.getItem(getSplitKey(email)));
    if (!isNaN(v) && v >= SPLIT_MIN && v <= SPLIT_MAX) return v;
  } catch { /* ignore */ }
  return SPLIT_DEFAULT;
}
function saveSplitRatio(email, ratio) {
  try { localStorage.setItem(getSplitKey(email), String(ratio)); } catch { /* ignore */ }
}

function siteLabel(s) {
  if (s.site_city) return `${s.site_name} (${s.site_city})`;
  if (s.supplier_site_code) return `${s.site_name} (${s.supplier_site_code})`;
  return s.site_name || 'Unnamed Site';
}

/* ══════════════════════════════════════════════════════════════════════════ */

export default function AssetRegistryPage() {
  const { callerEmail } = useUser();
  const { resolveProject } = useProject();

  /* ── Grid mode state ──────────────────────────────────────────────── */
  const {
    activeMode, setActiveMode,
    columnVisibility, toggleColumn, showAllColumns, hideAllColumns,
    pendingChanges, setPendingChange, clearPendingChanges, isDirty,
    sortKeys, setSortKeys,
    savedViews, activeViewId, activeViewName, saveView, deleteView, loadView,
  } = useGridState(callerEmail, 'asset_registry', ASSET_REGISTRY_COLUMNS);

  /* ── Left-panel collapse (per-user, per-screen) ────────────────────── */
  const { collapsed, toggle } = useLeftPanelCollapse(callerEmail, 'asset_registry');

  // Reset scroll on entry. Window-only: the scroll containers (.ar-detail-body,
  // .ar-table-wrap) live inside child components, so no inner ref is threaded.
  useScrollToTopOnMount();

  /* ── Sites ───────────────────────────────────────────────────────────── */
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [sitesLoading, setSitesLoading] = useState(true);

  /* ── Tools ───────────────────────────────────────────────────────────── */
  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [toolsError, setToolsError] = useState(null);

  /* ── Selection ───────────────────────────────────────────────────────── */
  const [selectedTool, setSelectedTool] = useState(null);

  /* ── Search / Filters ────────────────────────────────────────────────── */
  const [searchQuery, setSearchQuery] = useState('');
  // Server-side canonical search (search_inventory) when query >= min length;
  // null = not searching, fall back to the full loaded `tools` list.
  const [searchResults, setSearchResults] = useState(null);
  const searchDebounceRef = useRef(null);

  /* ── Splitter ────────────────────────────────────────────────────────── */
  const [splitRatio, setSplitRatio] = useState(() => loadSplitRatio(callerEmail));
  const draggingRef = useRef(false);
  const pageRef = useRef(null);

  /* ── Load sites on mount ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!callerEmail) return;
    setSitesLoading(true);
    getRegistrySites(callerEmail)
      .then(data => {
        const list = Array.isArray(data) ? data : data.sites || [];
        setSites(list);
        if (list.length === 1 && !selectedSite) {
          setSelectedSite(list[0].site_id);
        }
      })
      .catch(() => {})
      .finally(() => setSitesLoading(false));
  }, [callerEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Load tools when site changes ────────────────────────────────────── */
  useEffect(() => {
    if (!selectedSite || !callerEmail) {
      setTools([]);
      setSelectedTool(null);
      return;
    }
    setToolsLoading(true);
    setToolsError(null);
    setSelectedTool(null);
    resolveProject(selectedSite);
    getRegistryTools(selectedSite, callerEmail)
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setTools(list);
        if (list.length > 0) setSelectedTool(list[0]);
      })
      .catch(err => setToolsError(err.message))
      .finally(() => setToolsLoading(false));
  }, [selectedSite, callerEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Server-side canonical search (>= MIN_QUERY_LENGTH) ──────────────────
     Mirrors the S4/S5 reference (ToolListScreen): debounce 300ms, route to
     search_inventory; shorter queries clear results so the full loaded list
     (with the shared client filter) is shown instead. */
  useEffect(() => {
    const q = (searchQuery || '').trim();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!selectedSite || !callerEmail || q.length < MIN_QUERY_LENGTH) {
      setSearchResults(null);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      searchRegistryTools(selectedSite, q, callerEmail)
        .then(data => setSearchResults(Array.isArray(data) ? data : []))
        .catch(() => setSearchResults([]));
    }, 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery, selectedSite, callerEmail]);

  /* ── Splitter drag handlers ──────────────────────────────────────────── */
  const handleSplitterDown = useCallback(() => {
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    function onMove(ev) {
      if (!draggingRef.current || !pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();
      const ratio = (ev.clientX - rect.left) / rect.width;
      setSplitRatio(Math.max(SPLIT_MIN, Math.min(SPLIT_MAX, ratio)));
    }
    function onUp() {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setSplitRatio(prev => { saveSplitRatio(callerEmail, prev); return prev; });
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [callerEmail]);

  /* ── Sorted tools for grid view ───────────────────────────────────────── */
  const sortedTools = useMemo(
    () => sortRecords(tools, sortKeys),
    [tools, sortKeys]
  );

  /* ── Grid batch save ───────────────────────────────────────────────── */
  const [gridSaving, setGridSaving] = useState(false);
  const [gridSaveError, setGridSaveError] = useState(null);

  const reloadTools = useCallback(() => {
    if (!selectedSite || !callerEmail) return;
    getRegistryTools(selectedSite, callerEmail)
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setTools(list);
      })
      .catch(() => {});
  }, [selectedSite, callerEmail]);

  const handleGridSave = useCallback(async (pendingChangesMap) => {
    const changes = [];
    for (const [tool_id, fieldMap] of pendingChangesMap) {
      const tool_changes = { ...fieldMap };
      changes.push({ tool_id, tool_changes });
    }

    setGridSaving(true);
    setGridSaveError(null);

    try {
      const response = await apiFetch(`${API_BASE_URL}/batch_save_grid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          screen: 'asset_registry',
          caller_email: callerEmail,
          changes,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setGridSaveError(err.message || `Save failed (${response.status})`);
        return;
      }

      clearPendingChanges();
      setGridSaveError(null);
      reloadTools();
    } catch (err) {
      setGridSaveError(err.message || 'Network error');
    } finally {
      setGridSaving(false);
    }
  }, [callerEmail, clearPendingChanges, reloadTools]);

  /* ── Derived ─────────────────────────────────────────────────────────── */
  const selectedSiteObj = sites.find(s => s.site_id === selectedSite);
  const supplierName = selectedSiteObj?.supplier_name || tools[0]?.supplier_name || '';
  const hasDetail = !!selectedTool;
  const showGrid = activeMode === 'grid' && selectedSite;
  const leftPct = (hasDetail || showGrid) ? `${(splitRatio * 100).toFixed(1)}%` : '100%';

  return (
    <div className="ar-page">
      {/* Header strip */}
      <div className="ar-header">
        <h1 className="ar-header-title">Asset Registry</h1>
        <select
          className="ar-header-site-select"
          value={selectedSite}
          onChange={e => setSelectedSite(e.target.value)}
          disabled={sitesLoading}
        >
          <option value="">Select a site…</option>
          {sites.map(s => (
            <option key={s.site_id} value={s.site_id}>{siteLabel(s)}</option>
          ))}
        </select>
        {supplierName && <span className="ar-header-supplier">{supplierName}</span>}
        <div className="ar-header-spacer" />
        {/* Expand work area = collapse the left rail. Gated on right-pane content (no point collapsing when the left already owns 100%). */}
        {(showGrid || hasDetail) && (
          <LeftPanelCollapseToggle collapsed={collapsed} onToggle={toggle} />
        )}
        {selectedSite && (
          <GridModeToggle activeMode={activeMode} onChange={setActiveMode} />
        )}
      </div>

      {/* Two-pane body */}
      <div className="ar-body" ref={pageRef}>
        <div
          className={`ar-left${!hasDetail && !showGrid ? ' ar-left-full' : ''}${collapsed ? ' ar-left-collapsed' : ''}`}
          style={collapsed ? undefined : { width: leftPct }}
        >
          <AssetToolList
            tools={tools}
            searchResults={searchResults}
            loading={toolsLoading}
            error={toolsError}
            selectedSite={selectedSite}
            selectedTool={selectedTool}
            onSelectTool={setSelectedTool}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            siteName={selectedSiteObj ? siteLabel(selectedSiteObj) : ''}
          />
        </div>

        {showGrid ? (
          <>
            {!collapsed && <div className="ar-splitter" onMouseDown={handleSplitterDown} />}
            <div className="ar-right">
              <GridView
                records={tools}
                sortedRecords={sortedTools}
                sortKeys={sortKeys}
                setSortKeys={setSortKeys}
                columns={ASSET_REGISTRY_COLUMNS}
                screen="asset_registry"
                callerEmail={callerEmail}
                siteId={selectedSite}
                onSave={handleGridSave}
                onCancel={() => clearPendingChanges()}
                columnVisibility={columnVisibility}
                toggleColumn={toggleColumn}
                showAllColumns={showAllColumns}
                hideAllColumns={hideAllColumns}
                pendingChanges={pendingChanges}
                setPendingChange={setPendingChange}
                clearPendingChanges={clearPendingChanges}
                isDirty={isDirty}
                saving={gridSaving}
                saveError={gridSaveError}
                savedViews={savedViews}
                activeViewId={activeViewId}
                activeViewName={activeViewName}
                saveView={saveView}
                deleteView={deleteView}
                loadView={loadView}
              />
            </div>
          </>
        ) : hasDetail && (
          <>
            {!collapsed && <div className="ar-splitter" onMouseDown={handleSplitterDown} />}
            <div className="ar-right">
              <AssetToolDetail
                tool={selectedTool}
                callerEmail={callerEmail}
                onClose={() => setSelectedTool(null)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
